<template>
  <div :class="kpiContainerClass">
    <div v-for="(kpi, index) in kpis" :key="`${kpi?.id}kpi_${index}`">
      <div :class="kpiCardClass">
        <div
          class="kpi_gradient absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-t-xl"
        />

        <div class="flex flex-col gap-2 items-center justify-center w-full">
          <span
            class="text-xs font-medium secondary_color capitalize tracking-wide leading-snug text-center line-clamp-2"
          >
            {{ kpi.label }}
          </span>

          <a-tooltip :title="kpi.tooltipValue" placement="top">
            <span :class="kpiValueClass">
              {{ kpi.value }}
            </span>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import {
  formatLocaleNumber,
  formatFullNumber,
} from "@/src/utils/analyticsChartUtils.js";
import store from "@/src/store/index.js";

const props = defineProps({
  chartData: { type: Object, default: () => ({}) },
});

const chartConfiguration = computed(
  () => store.getters["analyticsConfiguration/renderedChartConfiguration"],
);

const rawKpis = computed(() => props.chartData?.kpis || []);

const kpis = computed(() => {
  const yAxisFields = chartConfiguration.value?.values || [];
  return rawKpis.value.map((kpi, index) => {
    const prefix = kpi.prefix || "";
    const suffix = kpi.suffix || "";
    const rawValue = kpi.value;
    const dp = yAxisFields[index]?.decimal_places;

    if (typeof rawValue === "number" || !isNaN(Number(rawValue))) {
      const formattedValue = formatLocaleNumber(rawValue, { decimalPlaces: dp });
      const tooltipValue = formatFullNumber(rawValue, prefix, suffix);
      return {
        ...kpi,
        value: `${prefix}${formattedValue}${suffix}`,
        tooltipValue,
      };
    }

    if (typeof rawValue === "string") {
      const hasPrefix = prefix && rawValue.startsWith(prefix);
      const hasSuffix = suffix && rawValue.endsWith(suffix);
      const displayValue = `${hasPrefix ? "" : prefix}${rawValue}${hasSuffix ? "" : suffix}`;
      return {
        ...kpi,
        value: displayValue,
        tooltipValue: displayValue,
      };
    }

    return { ...kpi, tooltipValue: kpi.value };
  });
});

const kpiCount = computed(() => kpis.value.length);

const kpiContainerClass = computed(() => {
  const count = kpiCount.value;
  const baseClasses =
    "grid gap-3 p-4 w-full h-full content-start overflow-auto";

  if (count === 1) {
    return `${baseClasses} grid-cols-1 max-w-[600px] mx-auto`;
  } else if (count === 2) {
    return `${baseClasses} grid-cols-1 md:grid-cols-2`;
  } else {
    return `${baseClasses} grid-cols-1 md:grid-cols-2 lg:grid-cols-3`;
  }
});

const kpiCardClass = computed(() => {
  const count = kpiCount.value;
  if (count === 1) {
    return "kpi_card bg_2 border_8 flex items-center justify-center rounded-xl p-8 transition-all duration-200 relative group hover:border-6 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer min-h-[100px] overflow-hidden";
  }
  return "kpi_card bg_2 border_8 flex items-center justify-center rounded-xl p-4 sm:p-5 transition-all duration-200 relative group hover:border-6 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer min-h-[100px] overflow-hidden";
});

const kpiValueClass = computed(() => {
  const count = kpiCount.value;
  if (count === 1) {
    return "text-4xl sm:text-[2.5rem] font-semibold primary_color leading-tight tabular-nums text-center break-all";
  }
  return "text-lg sm:text-xl sm:text-[1.75rem] font-semibold primary_color leading-tight tabular-nums text-center break-all";
});
</script>

<style scoped>
.kpi_gradient {
  background: linear-gradient(90deg, #7095eb, #b343cb);
}
</style>
